import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Newspaper, ExternalLink, RefreshCw, AlertTriangle } from "lucide-react";

const NEWS_QUERY = "cyclospora OR cyclosporiasis outbreak";
const GOOGLE_NEWS_RSS = `https://news.google.com/rss/search?q=${encodeURIComponent(NEWS_QUERY)}&hl=en-US&gl=US&ceid=US:en`;
const GOOGLE_NEWS_HUMAN = `https://news.google.com/search?q=${encodeURIComponent("cyclospora outbreak")}&hl=en-US`;
const ENC = encodeURIComponent(GOOGLE_NEWS_RSS);

// Ordered list of CORS proxies that return the raw RSS XML (fallbacks).
const PROXIES = [
  { url: `https://api.allorigins.win/raw?url=${ENC}`, type: "text" },
  { url: `https://api.allorigins.win/get?url=${ENC}`, type: "allorigins-json" },
  { url: `https://api.codetabs.com/v1/proxy/?quest=${ENC}`, type: "text" },
  { url: `https://thingproxy.freeboard.io/fetch/${GOOGLE_NEWS_RSS}`, type: "text" },
];

const fetchWithTimeout = (url, ms) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timer));
};

const splitSource = (title = "") => {
  const idx = title.lastIndexOf(" - ");
  if (idx > 0) return { headline: title.slice(0, idx), source: title.slice(idx + 3) };
  return { headline: title, source: "Google News" };
};

const timeAgo = (dateStr) => {
  const d = new Date((dateStr || "").replace(" ", "T"));
  if (isNaN(d)) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const parseRss = (xmlText) => {
  const xml = new DOMParser().parseFromString(xmlText, "text/xml");
  return Array.from(xml.querySelectorAll("item"))
    .slice(0, 12)
    .map((el) => ({
      title: el.querySelector("title")?.textContent || "",
      link: el.querySelector("link")?.textContent || "",
      pubDate: el.querySelector("pubDate")?.textContent || "",
    }));
};

// Primary: rss2json (structured JSON, reliable browser-side). Fallback: raw XML proxies.
async function fetchNews() {
  try {
    const res = await fetchWithTimeout(`https://api.rss2json.com/v1/api.json?rss_url=${ENC}`, 9000);
    const j = await res.json();
    if (j && j.status === "ok" && Array.isArray(j.items) && j.items.length) {
      return j.items.slice(0, 12).map((it) => ({ title: it.title, link: it.link, pubDate: it.pubDate }));
    }
  } catch (e) {
    /* fall through to raw proxies */
  }

  for (const p of PROXIES) {
    try {
      const res = await fetchWithTimeout(p.url, 8000);
      if (!res.ok) continue;
      let xmlText;
      if (p.type === "allorigins-json") {
        const jj = await res.json();
        xmlText = jj.contents;
      } else {
        xmlText = await res.text();
      }
      if (!xmlText || !xmlText.includes("<item")) continue;
      const items = parseRss(xmlText);
      if (items.length) return items;
    } catch (e) {
      /* try next proxy */
    }
  }
  return [];
}

export const GoogleNewsFeed = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | error

  const load = useCallback(() => {
    setStatus("loading");
    fetchNews()
      .then((list) => {
        if (list && list.length) {
          setItems(list);
          setStatus("ok");
        } else {
          setStatus("error");
        }
      })
      .catch(() => setStatus("error"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section id="news" className="relative px-4 py-16 md:py-24 max-w-6xl mx-auto" data-testid="news-section">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="stencil text-4xl md:text-6xl flex items-center gap-3">
            <Newspaper className="text-[#b026ff] w-9 h-9 md:w-12 md:h-12" /> LIVE OUTBREAK NEWS
          </h2>
          <p className="font-mono2 text-sm text-white/70 mt-2">
            Real, live headlines pulled straight from Google News. Yes, this parasite is genuinely in the news right now.
          </p>
        </div>
        <button onClick={load} data-testid="news-refresh" className="btn-hazard bg-[#d9b3ff] text-[#0a133f] px-4 py-2 self-start flex items-center gap-2 text-sm shrink-0">
          <RefreshCw size={16} className={status === "loading" ? "animate-spin" : ""} /> REFRESH
        </button>
      </div>

      {status === "loading" && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="news-loading">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="petri p-5 animate-pulse">
              <div className="h-4 bg-white/20 w-3/4 mb-3" />
              <div className="h-3 bg-white/10 w-full mb-2" />
              <div className="h-3 bg-white/10 w-2/3" />
            </div>
          ))}
        </div>
      )}

      {status === "error" && (
        <div className="petri p-8 text-center" data-testid="news-error">
          <AlertTriangle className="text-[#b026ff] mx-auto mb-3" size={32} />
          <p className="font-mono2 text-white/80 max-w-lg mx-auto">
            The live feed proxy is having explosive issues right now. But the outbreak is very real and
            very much in the news — see the latest headlines straight from Google News:
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-4">
            <a href={GOOGLE_NEWS_HUMAN} target="_blank" rel="noreferrer" data-testid="news-google-link" className="btn-hazard bg-[#d9b3ff] text-[#0a133f] px-5 py-2 flex items-center gap-2">
              OPEN GOOGLE NEWS <ExternalLink size={15} />
            </a>
            <button onClick={load} className="btn-hazard bg-white text-[#0a133f] px-5 py-2">TRY AGAIN</button>
          </div>
        </div>
      )}

      {status === "ok" && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="news-list">
          {items.map((it, i) => {
            const { headline, source } = splitSource(it.title);
            return (
              <motion.a
                key={i}
                href={it.link}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: Math.min(i * 0.05, 0.4) }}
                whileHover={{ y: -6 }}
                data-testid={`news-item-${i}`}
                className="petri p-5 flex flex-col gap-2 group"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono2 text-[10px] uppercase tracking-widest text-[#d9b3ff]">{source}</span>
                  <span className="font-mono2 text-[10px] text-white/50">{timeAgo(it.pubDate)}</span>
                </div>
                <h3 className="font-mono2 font-bold text-sm md:text-base text-white leading-snug group-hover:text-[#d9b3ff] transition-colors">
                  {headline}
                </h3>
                <span className="mt-auto pt-1 font-mono2 text-[10px] text-[#b026ff] flex items-center gap-1">
                  Read <ExternalLink size={11} />
                </span>
              </motion.a>
            );
          })}
        </div>
      )}

      {status === "ok" && (
        <div className="mt-6 text-center">
          <a href={GOOGLE_NEWS_HUMAN} target="_blank" rel="noreferrer" data-testid="news-viewall" className="font-mono2 text-xs uppercase tracking-widest text-[#d9b3ff] hover:text-white inline-flex items-center gap-1">
            View all on Google News <ExternalLink size={12} />
          </a>
        </div>
      )}
    </section>
  );
};

export default GoogleNewsFeed;
